import { ProductStockStatus, ProductType } from '../constants';
import { isProductGroupProductMember } from '../model';
import type {
  Product,
  ProductGroupView,
  ProductGroupViewMember,
  ProductGroupViewProductMember,
  VariationProduct,
} from '../model';

/**
 * A group member resolved by SKU. `GET /products` returns whatever the SKU names, so a member
 * SKU that belongs to a variant resolves to the `VariationProduct` itself rather than its parent.
 * A variation prices and stocks itself exactly like a simple product, so both branches below
 * handle it without a special case.
 */
type ProductGroupSibling = Product | VariationProduct;

/** `memberAttribute` is free-form on the Facade, so this is a display heuristic. */
const SWATCH_MEMBER_ATTRIBUTE = 'color';

/** Below this a group offers no choice, so the section renders nothing at all. */
const MIN_SWITCHABLE_SKU_COUNT = 2;

/** A row with one option is a label, not a choice, so it is dropped. */
const MIN_SWITCHABLE_ROW_OPTION_COUNT = 2;

export interface ProductGroupOption {
  /** Member label — the shade name or volume. */
  value: string;
  /** SKU this option navigates to. For a branch, its representative leaf. */
  sku: string;
  slug: string;
  name: string;
  imageUrl?: string;
  /** Raw size string off the representative product. */
  size?: string;
  originalPrice?: number;
  salePrice?: number;
  currency?: string;
  isCurrent: boolean;
  isOutOfStock: boolean;
  /**
   * Cheapest price under this member, for ordering only. The displayed price
   * comes from the representative leaf, which changes with the axis below —
   * sorting on that would reshuffle the row whenever the shopper switched size.
   */
  lowestPrice?: number;
}

/** One axis of the group. */
export interface ProductGroupRow {
  memberAttribute: string;
  /** True when members should render as image swatches instead of text chips. */
  isSwatchAxis: boolean;
  options: ProductGroupOption[];
  /** Label of the option matching the product being viewed, if it resolved. */
  currentValue?: string;
}

export interface ResolvedProductGroup {
  rows: ProductGroupRow[];
}

interface ResolveProductGroupOptionsParams {
  productGroup: ProductGroupView | null | undefined;
  /** Siblings fetched by SKU. Members with no match are treated as unavailable. */
  siblingProducts: ProductGroupSibling[];
  /** SKU of the product currently being viewed. */
  currentSku: string;
}

const collectLeafMembers = (member: ProductGroupViewMember): ProductGroupViewProductMember[] =>
  isProductGroupProductMember(member) ? [member] : member.members.flatMap(collectLeafMembers);

const findPricing = (product: ProductGroupSibling) => {
  if (product.type === ProductType.Variable) {
    // A Variable sibling has no price of its own; use its cheapest in-stock variation.
    // With none in stock, fall back to the cheapest overall — the option still renders,
    // struck through, and a priceless chip next to priced ones reads as a bug.
    const inStock = product.variations.filter((variation) => variation.status !== ProductStockStatus.outofstock);
    const pricedVariations = (inStock.length > 0 ? inStock : product.variations).map((variation) => ({
      originalPrice: variation.originalPrice,
      salePrice: variation.salePrice,
      effectivePrice: variation.salePrice ?? variation.originalPrice,
    }));

    if (pricedVariations.length === 0) {
      return {};
    }

    const cheapest = pricedVariations.reduce((lowest, candidate) =>
      candidate.effectivePrice < lowest.effectivePrice ? candidate : lowest,
    );

    return { originalPrice: cheapest.originalPrice, salePrice: cheapest.salePrice };
  }

  return { originalPrice: product.originalPrice, salePrice: product.salePrice };
};

const isProductOutOfStock = (product: ProductGroupSibling): boolean => {
  if (product.type !== ProductType.Variable) {
    return product.status === ProductStockStatus.outofstock;
  }

  return product.variations.every((variation) => variation.status === ProductStockStatus.outofstock);
};

const getEffectivePrice = (sku: string, siblingBySku: Map<string, ProductGroupSibling>): number | undefined => {
  const product = siblingBySku.get(sku);
  if (!product) {
    return undefined;
  }

  const { originalPrice, salePrice } = findPricing(product);
  return salePrice ?? originalPrice;
};

/**
 * The leaf a branch navigates to, and whose image and price represent it.
 * Prefers the leaf under the next-axis value the shopper is already on, so
 * switching shade keeps their size; otherwise the cheapest leaf, in-stock ones
 * first. A branch with nothing in stock is still represented, by its cheapest.
 */
const pickRepresentativeLeaf = (
  leaves: ProductGroupViewProductMember[],
  preferredNextValue: string | undefined,
  siblingBySku: Map<string, ProductGroupSibling>,
): ProductGroupViewProductMember | undefined => {
  // A branch whose leaves were all dropped server-side has nothing to represent it.
  if (leaves.length === 0) {
    return undefined;
  }

  const isAvailable = (leaf: ProductGroupViewProductMember) => {
    const product = siblingBySku.get(leaf.sku);
    return product !== undefined && !isProductOutOfStock(product);
  };

  const preferred = preferredNextValue
    ? leaves.find((leaf) => leaf.value === preferredNextValue && isAvailable(leaf))
    : undefined;
  if (preferred) {
    return preferred;
  }

  const available = leaves.filter(isAvailable);
  const pool = available.length > 0 ? available : leaves;

  return pool.reduce((cheapest, candidate) => {
    const candidatePrice = getEffectivePrice(candidate.sku, siblingBySku) ?? Number.POSITIVE_INFINITY;
    const cheapestPrice = getEffectivePrice(cheapest.sku, siblingBySku) ?? Number.POSITIVE_INFINITY;
    return candidatePrice < cheapestPrice ? candidate : cheapest;
  });
};

const toOption = (
  member: ProductGroupViewMember,
  {
    isCurrent,
    preferredNextValue,
    siblingBySku,
  }: {
    isCurrent: boolean;
    preferredNextValue: string | undefined;
    siblingBySku: Map<string, ProductGroupSibling>;
  },
): ProductGroupOption | null => {
  const leaves = collectLeafMembers(member);
  const representative = pickRepresentativeLeaf(leaves, preferredNextValue, siblingBySku);

  if (!representative) {
    return null;
  }

  const product = siblingBySku.get(representative.sku);
  const { originalPrice, salePrice } = product ? findPricing(product) : {};

  const leafPrices = leaves
    .map((leaf) => getEffectivePrice(leaf.sku, siblingBySku))
    .filter((price): price is number => price !== undefined);

  return {
    lowestPrice: leafPrices.length > 0 ? Math.min(...leafPrices) : undefined,
    value: member.value,
    sku: representative.sku,
    slug: representative.slug,
    name: representative.name,
    imageUrl: product?.productImageUrls?.[0],
    size: product?.size,
    originalPrice,
    salePrice,
    currency: product?.currency,
    isCurrent,
    // Unavailable only when every leaf is; an unresolved product is not buyable.
    isOutOfStock: leaves.every((leaf) => {
      const leafProduct = siblingBySku.get(leaf.sku);
      return !leafProduct || isProductOutOfStock(leafProduct);
    }),
  };
};

/**
 * Buyable options first, then cheapest first with unpriced ones last inside each
 * group. Sold-out siblings sink to the end of the row rather than sitting between
 * buyable ones, so a shopper scanning from the left meets only things they can buy.
 */
const byAvailabilityThenLowestPrice = (left: ProductGroupOption, right: ProductGroupOption) => {
  if (left.isOutOfStock !== right.isOutOfStock) {
    return left.isOutOfStock ? 1 : -1;
  }

  const leftPrice = left.lowestPrice ?? Number.POSITIVE_INFINITY;
  const rightPrice = right.lowestPrice ?? Number.POSITIVE_INFINITY;

  // Compared, not subtracted: two unpriced options are both Infinity, and
  // Infinity - Infinity is NaN — an inconsistent comparator.
  if (leftPrice === rightPrice) {
    return 0;
  }

  return leftPrice < rightPrice ? -1 : 1;
};

/** Chain of members from the top axis down to the leaf holding `sku`. */
const findPathToSku = (members: ProductGroupViewMember[], sku: string): ProductGroupViewMember[] | null => {
  for (const member of members) {
    if (isProductGroupProductMember(member)) {
      if (member.sku === sku) {
        return [member];
      }
      continue;
    }

    const descendantPath = findPathToSku(member.members, sku);
    if (descendantPath) {
      return [member, ...descendantPath];
    }
  }

  return null;
};

/**
 * Every purchasable SKU in the group, in tree order; empty when the group
 * cannot offer a choice. Shared by the RSC prefetch and the client gate so
 * both agree on when the section exists.
 */
export const getProductGroupMemberSkus = (productGroup: ProductGroupView | null | undefined): string[] => {
  if (!productGroup) {
    return [];
  }

  const skus = productGroup.members.flatMap(collectLeafMembers).map((leaf) => leaf.sku);

  return skus.length < MIN_SWITCHABLE_SKU_COUNT ? [] : skus;
};

/**
 * Turns a product group into one render-ready row per axis, following the path
 * to the product being viewed. Options are ordered buyable-first then cheapest
 * first, deliberately ignoring the Facade's `order` field.
 *
 * Returns `null` — render nothing — when no row ends up with a choice to make.
 */
export const resolveProductGroupOptions = ({
  productGroup,
  siblingProducts,
  currentSku,
}: ResolveProductGroupOptionsParams): ResolvedProductGroup | null => {
  if (!productGroup || getProductGroupMemberSkus(productGroup).length === 0) {
    return null;
  }

  const siblingBySku = new Map(siblingProducts.map((product) => [product.sku, product]));
  // An empty path — the viewed SKU is missing from its own group — still renders
  // the top axis, just with no option marked current.
  const path = findPathToSku(productGroup.members, currentSku) ?? [];

  const rows: ProductGroupRow[] = [];
  let levelMembers: ProductGroupViewMember[] = productGroup.members;
  let levelAttribute = productGroup.memberAttribute;
  let depth = 0;

  while (levelMembers.length > 0) {
    const currentMember = path[depth];
    // The next-axis value the shopper is on, kept wherever that combination exists.
    const preferredNextValue = path[depth + 1]?.value;

    const options = levelMembers
      .map((member) =>
        toOption(member, {
          isCurrent: member === currentMember,
          preferredNextValue,
          siblingBySku,
        }),
      )
      .filter((option): option is ProductGroupOption => option !== null)
      .sort(byAvailabilityThenLowestPrice);

    rows.push({
      memberAttribute: levelAttribute,
      isSwatchAxis: levelAttribute.trim().toLowerCase() === SWATCH_MEMBER_ATTRIBUTE,
      options,
      currentValue: options.find((option) => option.isCurrent)?.value,
    });

    if (!currentMember || isProductGroupProductMember(currentMember)) {
      break;
    }

    levelAttribute = currentMember.memberAttribute;
    levelMembers = currentMember.members;
    depth += 1;
  }

  // A single-option row offers no choice, so it is not worth rendering.
  const meaningfulRows = rows.filter((row) => row.options.length >= MIN_SWITCHABLE_ROW_OPTION_COUNT);

  return meaningfulRows.length > 0 ? { rows: meaningfulRows } : null;
};
